import React, { useState, useEffect } from 'react';
import {
  FiPlus as Plus,
  FiTrash2 as Trash2,
  FiEdit2 as Edit2,
  FiCheck as Check,
  FiX as X,
  FiAlertTriangle as AlertTriangle,
  FiRefreshCw as RefreshCw,
  FiDollarSign as DollarSign,
  FiToggleLeft as ToggleOff,
  FiToggleRight as ToggleOn,
  FiEye as Eye,
  FiEyeOff as EyeOff,
  FiExternalLink as ExternalLink,
  FiWifi as Wifi,
  FiCloud as Cloud,
} from 'react-icons/fi';
import {
  getCredentials,
  createCredential,
  updateCredential,
  deleteCredential,
  testCredentialConnection,
  testSavedCredential,
  getAvailableModels,
  getAwsAccounts,
  autoConfigureBedrock,
} from '../../../services/aiOperationsApi';
import { awsEnvironmentsAPI } from '../../../services/realApi';
import type { LLMCredential, TestConnectionResult, AwsEnvironment, PlatformAwsAccount, ProviderField } from './llmProviders/types';
import PROVIDERS from './llmProviders/providers';
import { ProviderInstructions, ProviderLogo } from './llmProviders/ProviderComponents';

const LLMProvidersTab: React.FC = () => {
  const [credentials, setCredentials] = useState<LLMCredential[]>([]);
  const [availableModels, setAvailableModels] = useState<{ provider: string; model_id?: string; name?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState<LLMCredential | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [credentialName, setCredentialName] = useState('');
  const [credentialFields, setCredentialFields] = useState<Record<string, string>>({});
  const [bedrockAuthMethod, setBedrockAuthMethod] = useState('account_connection');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

  // AWS Environment state (for Bedrock evaluation configuration)
  const [awsEnvironments, setAwsEnvironments] = useState<AwsEnvironment[]>([]);
  const [selectedAwsEnvironment, setSelectedAwsEnvironment] = useState<AwsEnvironment | null>(null);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);

  // Platform AWS accounts (for account_connection auth)
  const [platformAwsAccounts, setPlatformAwsAccounts] = useState<PlatformAwsAccount[]>([]);
  const [loadingPlatformAccounts, setLoadingPlatformAccounts] = useState(false);

  useEffect(() => {
    initializeProviders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeProviders = async () => {
    try {
      await autoConfigureBedrock();
    } catch (err: unknown) {
      // Non-critical — page works without auto-configure
      console.error('Bedrock auto-configure failed (non-critical)', err);
    }
    await loadCredentials();
  };

  // Load AWS Environments when Bedrock is selected
  useEffect(() => {
    if (selectedProvider === 'bedrock' && showModal) {
      loadAwsEnvironments();
      loadPlatformAwsAccounts();
    }
  }, [selectedProvider, showModal]);

  const loadAwsEnvironments = async () => {
    setLoadingEnvironments(true);
    try {
      const result = await awsEnvironmentsAPI.getEnvironments();
      if (result.success) {
        setAwsEnvironments(result.data || []);
      }
    } catch (err: unknown) {
      console.error('Error loading AWS environments:', err);
    } finally {
      setLoadingEnvironments(false);
    }
  };

  const loadPlatformAwsAccounts = async () => {
    setLoadingPlatformAccounts(true);
    try {
      const result = await getAwsAccounts();
      setPlatformAwsAccounts(result?.accounts || []);
    } catch (err: unknown) {
      console.error('Error loading platform AWS accounts:', err);
    } finally {
      setLoadingPlatformAccounts(false);
    }
  };

  const handleAwsEnvironmentSelect = (environmentId: string) => {
    if (!environmentId) {
      setSelectedAwsEnvironment(null);
      return;
    }

    const environment = awsEnvironments.find(env => env.id === environmentId);
    if (environment?.aws_account) {
      setSelectedAwsEnvironment(environment);

      // Pre-populate evaluation fields with suggested values based on the AWS account
      const accountId = environment.aws_account.account_id;
      const suggestedRoleArn = `arn:aws:iam::${accountId}:role/BedrockEvaluationRole`;
      const suggestedBucket = `${accountId}-bedrock-evaluation-outputs`;

      setCredentialFields(prev => ({
        ...prev,
        evaluation_role_arn: prev.evaluation_role_arn || suggestedRoleArn,
        evaluation_output_bucket: prev.evaluation_output_bucket || suggestedBucket,
      }));
    }
  };

  const loadCredentials = async () => {
    setLoading(true);
    setError(null);
    try {
      const [credentialsData, modelsData] = await Promise.all([
        getCredentials(),
        getAvailableModels()
      ]);
      setCredentials(credentialsData || []);
      setAvailableModels(modelsData?.models || modelsData || []);
    } catch (err: unknown) {
      console.error('Error loading credentials:', err);
      setError(err instanceof Error ? err.message : 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedProvider('openai');
    setCredentialName('');
    setCredentialFields({});
    setBedrockAuthMethod('account_connection');
    setMonthlyLimit('');
    setEditingCredential(null);
    setShowSecrets({});
    setTestResult(null);
    setSelectedAwsEnvironment(null);
  };

  const openCreateModal = (provider: string | null = null) => {
    resetForm();
    if (provider) {
      setSelectedProvider(provider);
    }
    setShowModal(true);
  };

  const openEditModal = (credential: LLMCredential) => {
    setEditingCredential(credential);
    setSelectedProvider(credential.provider);
    setCredentialName(credential.name || '');

    // Pre-populate non-secret fields from masked_credentials
    // Secret fields (type: 'password') should start empty
    const providerConfig = PROVIDERS[credential.provider];
    const authMethod = credential.bedrock_auth_method || 'role_assumption';
    const fields = credential.provider === 'bedrock'
      ? (providerConfig.fields as Record<string, ProviderField[]>)[authMethod] || []
      : providerConfig.fields || [];

    const nonSecretFields: Record<string, string> = {};
    (fields as ProviderField[]).forEach((field: ProviderField) => {
      if (field.type !== 'password' && credential.masked_credentials?.[field.key]) {
        nonSecretFields[field.key] = credential.masked_credentials[field.key];
      }
    });

    setCredentialFields(nonSecretFields);
    setBedrockAuthMethod(authMethod);
    setMonthlyLimit(credential.monthly_limit_usd?.toString() || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // When editing, filter out empty secret fields so backend keeps existing values
      let filteredCredentials = { ...credentialFields };
      if (editingCredential) {
        const fields = getFieldsForProvider();
        (fields as ProviderField[]).forEach((field: ProviderField) => {
          if (field.type === 'password' && !filteredCredentials[field.key]) {
            delete filteredCredentials[field.key];
          }
        });
      }

      // If all credentials were filtered out (no updates), set to null so backend skips validation
      const hasCredentialUpdates = Object.keys(filteredCredentials).length > 0;

      const data = {
        provider: selectedProvider,
        name: credentialName || `${PROVIDERS[selectedProvider].name} Credentials`,
        credentials: hasCredentialUpdates ? filteredCredentials : null,
        monthly_limit_usd: monthlyLimit ? Number.parseFloat(monthlyLimit) : null,
      };

      if (selectedProvider === 'bedrock') {
        (data as Record<string, unknown>).bedrock_auth_method = bedrockAuthMethod;
      }

      if (editingCredential) {
        // Update existing
        await updateCredential(editingCredential.id, data as Record<string, unknown>);
      } else {
        // Create new - credentials are required
        if (!hasCredentialUpdates) {
          throw new Error('Please fill in the required credential fields');
        }
        await createCredential(data as Record<string, unknown>);
      }

      setShowModal(false);
      resetForm();
      await loadCredentials();
    } catch (err: unknown) {
      console.error('Error saving credential:', err);
      setError(err instanceof Error ? err.message : 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const fields = getFieldsForProvider();
      const secretFields = (fields as ProviderField[]).filter((f: ProviderField) => f.type === 'password');
      const hasAnySecretFilled = secretFields.some((f: ProviderField) => credentialFields[f.key]);

      // If editing and no new secret credentials entered, test the saved credentials
      if (editingCredential && !hasAnySecretFilled && secretFields.length > 0) {
        const result = await testSavedCredential(editingCredential.id);
        setTestResult({
          success: result.success,
          message: result.message
        });
        return;
      }

      // For new credentials or when new secret values are provided
      let testCredentials = { ...credentialFields };

      // Check if we have the required fields
      const requiredFields = (fields as ProviderField[]).filter((f: ProviderField) => !f.key.includes('optional') && !f.label.includes('Optional'));
      const missingRequired = requiredFields.filter((f: ProviderField) => !testCredentials[f.key]);

      if (missingRequired.length > 0) {
        setTestResult({
          success: false,
          message: `Please fill in: ${missingRequired.map(f => f.label).join(', ')}`
        });
        return;
      }

      const data: Record<string, unknown> = {
        provider: selectedProvider,
        credentials: testCredentials,
      };

      if (selectedProvider === 'bedrock') {
        data.bedrock_auth_method = bedrockAuthMethod;
      }

      const result = await testCredentialConnection(data);
      setTestResult({
        success: result.success,
        message: result.message,
        accessible_models: result.accessible_models || [],
        details: result.details || {}
      });
    } catch (err: unknown) {
      console.error('Error testing connection:', err);
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test connection'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (credential: LLMCredential) => {
    if (!globalThis.confirm(`Are you sure you want to delete the ${PROVIDERS[credential.provider]?.name || credential.provider} credentials? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteCredential(credential.id);
      await loadCredentials();
    } catch (err: unknown) {
      console.error('Error deleting credential:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete credential');
    }
  };

  const handleToggleEnabled = async (credential: LLMCredential) => {
    try {
      await updateCredential(credential.id, { is_enabled: !credential.is_enabled });
      await loadCredentials();
    } catch (err: unknown) {
      console.error('Error toggling credential:', err);
      setError(err instanceof Error ? err.message : 'Failed to update credential');
    }
  };

  const getFieldsForProvider = () => {
    const provider = PROVIDERS[selectedProvider];
    // Handle providers with multiple auth methods (bedrock, sagemaker)
    if (provider?.authMethods) {
      return (provider.fields as Record<string, ProviderField[]>)[bedrockAuthMethod] || [];
    }
    return provider?.fields || [];
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Get configured providers
  const configuredProviders = new Set(credentials.map(c => c.provider));
  const unconfiguredProviders = Object.keys(PROVIDERS).filter(p => !configuredProviders.has(p));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            LLM Provider Credentials
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Configure API credentials to test prompts directly against LLM providers
          </p>
        </div>
        <button
          onClick={loadCredentials}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Configured Providers */}
      {credentials.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Configured Providers</h3>
          <div className="grid gap-4">
            {credentials.map((credential) => {
              const providerConfig = PROVIDERS[credential.provider];
              let authMethodLabel: string;
              if (credential.bedrock_auth_method === 'role_assumption') {
                authMethodLabel = 'IAM Role Assumption';
              } else if (credential.bedrock_auth_method === 'account_connection') {
                authMethodLabel = 'AWS Connection';
              } else if (credential.bedrock_auth_method === 'environment') {
                authMethodLabel = 'Environment Credentials';
              } else {
                authMethodLabel = 'Access Keys';
              }
              return (
                <div
                  key={credential.id}
                  className={`bg-white dark:bg-gray-800 rounded-lg border p-4 ${
                    credential.is_enabled
                      ? 'border-gray-200 dark:border-gray-700'
                      : 'border-gray-200 dark:border-gray-700 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <ProviderLogo provider={credential.provider} />
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {providerConfig?.name || credential.provider}
                          </h4>
                          {!credential.is_enabled && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                              Disabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {credential.name || 'Default credentials'}
                        </p>
                        {credential.bedrock_auth_method && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Auth: {authMethodLabel}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Spending */}
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-sm">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            ${credential.current_month_usage_usd?.toFixed(2) || '0.00'}
                          </span>
                          {credential.monthly_limit_usd && (
                            <span className="text-gray-500 dark:text-gray-400">
                              / ${credential.monthly_limit_usd?.toFixed(0)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">This month</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleEnabled(credential)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title={credential.is_enabled ? 'Disable' : 'Enable'}
                        >
                          {credential.is_enabled ? (
                            <ToggleOn className="w-5 h-5 text-green-500" />
                          ) : (
                            <ToggleOff className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => openEditModal(credential)}
                          className="p-2 text-gray-400 hover:text-blue-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(credential)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Masked credentials preview */}
                  {credential.masked_credentials && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {Object.entries(credential.masked_credentials).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-gray-400 dark:text-gray-500">{key}:</span>{' '}
                            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{value}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unconfigured Providers */}
      {unconfiguredProviders.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {credentials.length > 0 ? 'Add More Providers' : 'Available Providers'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {unconfiguredProviders.map((providerId) => {
              const provider = PROVIDERS[providerId];
              // Count models for this provider from dynamic API data
              const providerModelCount = availableModels.filter(m => m.provider === providerId).length;
              return (
                <button
                  key={providerId}
                  onClick={() => openCreateModal(providerId)}
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-left group"
                >
                  <ProviderLogo provider={providerId} />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      {provider.name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {providerModelCount} models available
                    </p>
                  </div>
                  <Plus className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 ml-auto" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {credentials.length === 0 && unconfiguredProviders.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">
            All providers have been configured.
          </p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingCredential ? 'Edit' : 'Configure'} {PROVIDERS[selectedProvider]?.name} Credentials
              </h3>

              {/* Provider Instructions */}
              <ProviderInstructions
                provider={selectedProvider}
                authMethod={bedrockAuthMethod}
              />

              {/* Error in modal */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Provider Selector (only for new) */}
                {!editingCredential && (
                  <div>
                    <label htmlFor="provider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Provider
                    </label>
                    <select id="provider"
                      value={selectedProvider}
                      onChange={(e) => {
                        setSelectedProvider(e.target.value);
                        setCredentialFields({});
                        setBedrockAuthMethod('role_assumption');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {Object.entries(PROVIDERS).map(([id, config]) => (
                        <option key={id} value={id} disabled={configuredProviders.has(id)}>
                          {config.name} {configuredProviders.has(id) ? '(Already configured)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label htmlFor="display-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Display Name (optional)
                  </label>
                  <input id="display-name"
                    type="text"
                    value={credentialName}
                    onChange={(e) => setCredentialName(e.target.value)}
                    placeholder={`${PROVIDERS[selectedProvider]?.name} Production`}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Auth Method Selector (for providers with multiple auth methods) */}
                {PROVIDERS[selectedProvider]?.authMethods && (
                  <div>
                    <label htmlFor="authentication-method" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Authentication Method
                    </label>
                    <select id="authentication-method"
                      value={bedrockAuthMethod}
                      onChange={(e) => {
                        setBedrockAuthMethod(e.target.value);
                        setCredentialFields({});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {PROVIDERS[selectedProvider].authMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* AWS Environment Helper for Bedrock Evaluations */}
                {selectedProvider === 'bedrock' && awsEnvironments.length > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Cloud className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <h4 className="font-medium text-blue-900 dark:text-blue-200 text-sm">
                        Bedrock Evaluations Setup
                      </h4>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                      Select an AWS Environment to auto-populate evaluation configuration.
                      You can also configure these fields manually below.
                    </p>
                    <select
                      value={selectedAwsEnvironment?.id || ''}
                      onChange={(e) => handleAwsEnvironmentSelect(e.target.value)}
                      disabled={loadingEnvironments}
                      className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="">Select AWS Environment (optional)</option>
                      {awsEnvironments.map((env) => (
                        <option key={env.id} value={env.id}>
                          {env.name} ({env.aws_account?.account_id || 'Unknown Account'})
                        </option>
                      ))}
                    </select>
                    {selectedAwsEnvironment && (
                      <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                        Using account: {selectedAwsEnvironment.aws_account?.account_id}
                      </div>
                    )}
                  </div>
                )}

                {/* Dynamic Credential Fields */}
                {(getFieldsForProvider() as ProviderField[]).map((field: ProviderField) => {
                  const isSecretField = field.type === 'password';
                  const isEditing = !!editingCredential;
                  // For secret fields in edit mode, show placeholder indicating they need to re-enter
                  const placeholder = isSecretField && isEditing
                    ? 'Enter new value to replace existing'
                    : field.placeholder;

                  // Special rendering for AWS account select
                  if (field.type === 'aws_account_select') {
                    return (
                      <div key={field.key}>
                        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {field.label}
                        </span>
                        {loadingPlatformAccounts && (
                          <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Loading AWS accounts...
                          </div>
                        )}
                        {!loadingPlatformAccounts && platformAwsAccounts.length > 0 && (
                          <select
                            value={credentialFields[field.key] || ''}
                            onChange={(e) => setCredentialFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select an AWS account...</option>
                            {platformAwsAccounts.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.account_id} - {account.role_name} ({account.account_type || 'default'})
                              </option>
                            ))}
                          </select>
                        )}
                        {!loadingPlatformAccounts && platformAwsAccounts.length === 0 && (
                          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                              No AWS accounts configured. Go to Settings &gt; AWS Accounts to add one, or use IAM Role Assumption instead.
                            </p>
                          </div>
                        )}
                        {field.description && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {field.description}
                          </p>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={field.key}>
                      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {field.label}
                        {isSecretField && isEditing && (
                          <span className="ml-2 text-xs text-amber-600 dark:text-amber-400 font-normal">
                            (leave empty to keep existing)
                          </span>
                        )}
                      </span>
                      <div className="relative">
                        <input
                          type={isSecretField && !showSecrets[field.key] ? 'password' : 'text'}
                          value={credentialFields[field.key] || ''}
                          onChange={(e) => setCredentialFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                          placeholder={placeholder}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        {isSecretField && (
                          <button
                            type="button"
                            onClick={() => toggleShowSecret(field.key)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      {field.description && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {field.description}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Monthly Limit */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monthly Spending Limit (USD)
                  </span>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      value={monthlyLimit}
                      onChange={(e) => setMonthlyLimit(e.target.value)}
                      placeholder="100.00"
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Leave empty for no limit. Prompt testing will be blocked once limit is reached.
                  </p>
                </div>
              </div>

              {/* Test Connection Result */}
              {testResult && (
                <div className={`mt-4 p-3 rounded-lg ${
                  testResult.success
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-start gap-2">
                    {testResult.success ? (
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    )}
                    <p className={`text-sm ${
                      testResult.success
                        ? 'text-green-800 dark:text-green-300'
                        : 'text-red-800 dark:text-red-300'
                    }`}>
                      {testResult.message}
                    </p>
                  </div>

                  {/* Bedrock Accessible Models */}
                  {selectedProvider === 'bedrock' && (testResult.accessible_models?.length ?? 0) > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                      <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-2">
                        Models with ON_DEMAND access enabled:
                      </p>
                      <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
                        {testResult.accessible_models!.map((model) => (
                          <div key={model.model_id} className="text-xs text-green-600 dark:text-green-400 truncate" title={model.model_id}>
                            {model.model_name || model.model_id}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bedrock Warning: No accessible models */}
                  {selectedProvider === 'bedrock' && testResult.success && testResult.details?.accessible_models === 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
                      <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/30 rounded">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-700 dark:text-amber-300">
                          <p className="font-medium">No models have ON_DEMAND access enabled</p>
                          <p className="mt-1">
                            Go to AWS Bedrock Console → Model access → Manage model access to enable the models you want to use.
                          </p>
                          <a
                            href={`https://${credentialFields.region || 'us-east-1'}.console.aws.amazon.com/bedrock/home?region=${credentialFields.region || 'us-east-1'}#/modelaccess`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-amber-600 dark:text-amber-400 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open Bedrock Model Access
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <button
                    onClick={handleTestConnection}
                    disabled={testing || saving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {testing ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <Wifi className="w-4 h-4" />
                        Test Connection
                      </>
                    )}
                  </button>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || testing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        {editingCredential ? 'Update' : 'Save'} Credentials
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
          About LLM Provider Credentials
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
          <li>Credentials are encrypted at rest using AES-256 encryption</li>
          <li>All users in your organization can use these credentials for prompt testing</li>
          <li>Set monthly spending limits to control costs</li>
          <li>For AWS Bedrock, IAM Role Assumption is recommended for better security</li>
        </ul>
      </div>
    </div>
  );
};

export default LLMProvidersTab;
